<script setup lang="ts">
import { useManualOutstandingMutations } from '@/composables/mutations/tertiary/useManualOutstandingMutations';
import { useStudents } from '@/composables/queries/useStudent';
import { useCourses } from '@/composables/queries/useCourses';
import { EnrollmentStatusEnum } from '@/types/composition-functions';

const emit = defineEmits<{ success: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { bulkAddByStudents, bulkAddingByStudents } = useManualOutstandingMutations();

const studentFilters = ref({
  first: 50,
  page: 1,
  filter: { search: '', status: EnrollmentStatusEnum.Enrolled },
});
const { students, loadStudents } = useStudents({
  getStudentsOptions: { autoFetch: false },
  studentFilters: studentFilters as never,
});

let studentDebounce: ReturnType<typeof setTimeout> | null = null;
function onStudentFilter(event: { value: string }) {
  if (studentDebounce) clearTimeout(studentDebounce);
  studentDebounce = setTimeout(() => {
    studentFilters.value = { first: 50, page: 1, filter: { search: event.value, status: EnrollmentStatusEnum.Enrolled } };
    loadStudents();
  }, 300);
}

const studentOptions = computed(() =>
  students.data.value.map((s) => ({ label: `${s.matric_no} - ${s.surname} ${s.first_name}`, value: s.id })),
);

const { courses, setFilter: setCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });
let courseDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (courseDebounce) clearTimeout(courseDebounce);
  courseDebounce = setTimeout(() => { setCourseFilter({ query: event.value }); }, 300);
}

const courseOptions = computed(() =>
  courses.value.map((c: { id: string; course_code: string; course_title: string; unit: number }) => ({
    label: `${c.course_code} — ${c.course_title} (${c.unit}u)`, value: c.id,
    code: c.course_code, title: c.course_title, unit: c.unit,
  })),
);

const selectedStudents = ref<string[]>([]);
const selectedCourses = ref<string[]>([]);
const status = ref<'C' | 'E'>('C');
const curriculumLevel = ref('');
const reason = ref('');

const statusOptions = [
  { label: 'Compulsory', value: 'C' },
  { label: 'Elective', value: 'E' },
];

async function handleSubmit() {
  if (!selectedStudents.value.length || !selectedCourses.value.length) return;
  const result = await bulkAddByStudents({
    student_ids: selectedStudents.value,
    course_ids: selectedCourses.value,
    status: status.value,
    curriculum_level: curriculumLevel.value || undefined,
    reason: reason.value || undefined,
  });
  if (result?.success) {
    resetForm();
    visible.value = false;
    emit('success');
  }
}

function resetForm() {
  selectedStudents.value = [];
  selectedCourses.value = [];
  status.value = 'C';
  curriculumLevel.value = '';
  reason.value = '';
}
</script>

<template>
  <Dialog v-model:visible="visible" header="Assign Outstanding to Students" modal :style="{ width: '36rem' }" @hide="resetForm">
    <div class="flex flex-col gap-4 pt-2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Students *</label>
        <MultiSelect v-model="selectedStudents" :options="studentOptions" optionLabel="label" optionValue="value"
          placeholder="Search and select students..." filter @filter="onStudentFilter" :maxSelectedLabels="3" class="w-full" />
        <small class="text-gray-500">{{ selectedStudents.length }} student(s) selected</small>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Courses *</label>
        <MultiSelect v-model="selectedCourses" :options="courseOptions" optionLabel="label" optionValue="value"
          placeholder="Search courses..." filter @filter="onCourseFilter" :maxSelectedLabels="3" class="w-full">
          <template #option="{ option }">
            <div class="flex flex-col py-0.5">
              <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
              <span class="text-xs text-gray-400">{{ option.unit }} unit(s)</span>
            </div>
          </template>
        </MultiSelect>
        <small class="text-gray-500">{{ selectedCourses.length }} course(s) selected</small>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Type *</label>
        <SelectButton v-model="status" :options="statusOptions" optionLabel="label" optionValue="value" :allowEmpty="false" />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Original Level (optional)</label>
        <InputText v-model="curriculumLevel" placeholder="e.g., 100, 200" class="w-full" />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Reason (optional)</label>
        <Textarea v-model="reason" placeholder="Why these courses are outstanding..." rows="2" class="w-full" />
      </div>
    </div>

    <template #footer>
      <Button size="small" label="Cancel" severity="secondary" text @click="visible = false" />
      <Button size="small" label="Assign Outstanding" icon="pi pi-check" @click="handleSubmit"
        :loading="bulkAddingByStudents" :disabled="!selectedStudents.length || !selectedCourses.length" />
    </template>
  </Dialog>
</template>
